/**
 * Proveedor de cotizaciones de mercado en cliente (spec/05-security.md §4.4, FR-MKT-1..8).
 *
 * Principios no negociables:
 *  - Ningun dato financiero ni saldo sale del dispositivo: QuoteRequest solo contiene
 *    instrumentId y asOf (INV-7, FR-MKT-1).
 *  - Sin secreto de servidor (spec/02-architecture.md §8 decision 6).
 *  - Precios manuales introducidos por el usuario prevalecen sobre precios externos (FR-MKT-7).
 *  - Degradacion limpia ante fallo de red o simbolo desconocido (FR-MKT-4).
 */

import type { PriceQuote, QuoteProvider, QuoteRequest } from "./quote-provider.js";

export interface RealQuoteProviderOptions {
  readonly fetchImpl?: typeof fetch;
}

/**
 * Mapea el símbolo de activo y bolsa al formato canónico de cotización de Yahoo Finance.
 * En bolsas fuera de EE.UU. (Shenzhen, Shanghai, Hong Kong, Tokio, Xetra, Londres, BVL, etc.),
 * Yahoo Finance requiere el sufijo de mercado (ej: 000001.SZ, 00001.HK, 7203.T, SAP.DE, ALICORC1.LM).
 */
export function getYahooTicker(symbol: string, exchange?: string): string {
  const cleanSym = symbol.trim().toUpperCase();
  if (cleanSym.includes(".")) return cleanSym;
  const ex = (exchange || "").toUpperCase();

  // Bolsas de China continental y Hong Kong
  if (ex.includes("SZSE") || ex === "SHENZHEN") return `${cleanSym}.SZ`;
  if (ex.includes("SSE") || ex === "SHANGHAI") return `${cleanSym}.SS`;
  if (ex.includes("HKEX") || ex === "HONG KONG") return `${cleanSym}.HK`;

  // Bolsas de Japón y Corea
  if (ex.includes("TSE") || ex === "TOKYO" || ex === "JPX") return `${cleanSym}.T`;
  if (ex.includes("KRX") || ex === "KOSPI") return `${cleanSym}.KS`;
  if (ex.includes("KOSDAQ")) return `${cleanSym}.KQ`;

  // Bolsas de Europa
  if (ex.includes("LSE") || ex === "LONDON") return `${cleanSym}.L`;
  if (ex.includes("XETRA") || ex.includes("FSX") || ex === "FRANKFURT") return `${cleanSym}.DE`;
  if (ex.includes("EURONEXT")) return `${cleanSym}.AS`;
  if (ex.includes("BME") || ex === "MADRID") return `${cleanSym}.MC`;
  if (ex.includes("SIX") || ex.includes("SWX") || ex === "SWISS") return `${cleanSym}.SW`;

  // Bolsas de América Latina
  if (ex.includes("B3") || ex === "SAO PAULO") return `${cleanSym}.SA`;
  if (ex.includes("BMV") || ex === "MEXICO") return `${cleanSym}.MX`;
  if (ex.includes("BVL") || ex === "LIMA") {
    // Si ya cotiza en NYSE (ADR) no lleva .LM
    if (["BAP", "SCCO", "IFS", "BVN"].includes(cleanSym)) return cleanSym;
    return `${cleanSym}.LM`;
  }

  // Bolsas de India, Canadá y Australia
  if (ex.includes("BSE_IN") || ex === "BSE") return `${cleanSym}.BO`;
  if (ex.includes("NSE_IN") || ex === "NSE") return `${cleanSym}.NS`;
  if (ex.includes("TSX") || ex === "TORONTO") return `${cleanSym}.TO`;
  if (ex.includes("TSXV")) return `${cleanSym}.V`;
  if (ex.includes("ASX") || ex === "AUSTRALIA") return `${cleanSym}.AX`;

  return cleanSym;
}

export class RealQuoteProvider implements QuoteProvider {
  private readonly manualQuotes = new Map<string, PriceQuote>();
  private readonly fetchImpl: typeof fetch;

  constructor(options: RealQuoteProviderOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private key(instrumentId: string, asOf: string): string {
    return `${instrumentId}|${asOf}`;
  }

  /**
   * Registra una cotizacion manual que prevalece sobre el proveedor externo (FR-MKT-7).
   */
  setManualQuote(quote: PriceQuote): void {
    this.manualQuotes.set(this.key(quote.instrumentId, quote.asOf), {
      ...quote,
      manual: true,
    });
  }

  async getQuote(request: QuoteRequest): Promise<PriceQuote | undefined> {
    const k = this.key(request.instrumentId, request.asOf);

    // 1. Verificar si hay precio manual (prevalece)
    const manual = this.manualQuotes.get(k);
    if (manual) return manual;

    // 2. Construir candidatos de consulta en Yahoo Finance
    const candidates = [request.instrumentId];
    if (!request.instrumentId.includes(".")) {
      // Si el símbolo es numérico (típico de bolsas asiáticas como Shenzhen, Shanghai, Hong Kong, Tokio)
      if (/^\d+$/.test(request.instrumentId)) {
        candidates.push(`${request.instrumentId}.SZ`, `${request.instrumentId}.SS`, `${request.instrumentId}.HK`, `${request.instrumentId}.T`);
      } else {
        candidates.push(`${request.instrumentId}.LM`, `${request.instrumentId}.DE`, `${request.instrumentId}.L`, `${request.instrumentId}.SA`);
      }
    }

    // 3. Consulta pública externa
    for (const cand of candidates) {
      try {
        const symbol = encodeURIComponent(cand);
        const url = `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?range=1d&interval=1d`;

        const res = await this.fetchImpl(url, { method: "GET" });
        if (!res.ok) continue;

        const data = (await res.json()) as {
          chart?: {
            result?: Array<{
              meta?: {
                regularMarketPrice?: number;
                regularMarketVolume?: number;
                chartPreviousClose?: number;
                currency?: string;
              };
            }>;
          };
        };

        const meta = data.chart?.result?.[0]?.meta;
        if (meta && typeof meta.regularMarketPrice === "number" && !isNaN(meta.regularMarketPrice)) {
          // Convertir a unidades minimas (escala 2)
          const scale = 2;
          const priceMinor = BigInt(Math.round(meta.regularMarketPrice * 10 ** scale));
          const currency = meta.currency ? meta.currency.toUpperCase() : "USD";
          const volume = typeof meta.regularMarketVolume === "number" && !isNaN(meta.regularMarketVolume)
            ? meta.regularMarketVolume
            : undefined;
          const changePercent = typeof meta.chartPreviousClose === "number" && meta.chartPreviousClose > 0
            ? ((meta.regularMarketPrice - meta.chartPreviousClose) / meta.chartPreviousClose) * 100
            : undefined;

          return {
            instrumentId: request.instrumentId,
            asOf: request.asOf,
            priceMinor,
            currency,
            scale,
            source: "Yahoo Finance",
            retrievedAt: new Date().toISOString(),
            manual: false,
            volume,
            changePercent,
          };
        }
      } catch {
        // Continuar con el siguiente candidato ante fallo de red
      }
    }

    return undefined;
  }
}
